import { PropsWithChildren, createContext, useContext } from "react";

import { RootComponentInstance } from "@uniformdev/canvas";

import { UserSegmentTypes } from "../components/personalization-container/src/use-user-segments";

import { UniformEnvironment } from "./index";

/**
 * WAC Environment types - represents the deployment environment for WAC
 */
export type WACEnvironment = "development" | "staging" | "production";

/**
 * Contentful Environment types - represents available Contentful environments
 */
export type ContentfulEnvironment = "master" | "staging";

/**
 * WAE (Web Application Environment) Context Type
 * Provides environment configuration and composition data throughout the application
 */
export type WAEContextType = {
    /** WAC deployment environment */
    wacEnvironment: WACEnvironment;
    /** Uniform CMS environment */
    uniformEnvironment: UniformEnvironment;
    /** Uniform project identifier */
    uniformProjectId: string;
    /** Whether running in Uniform Canvas editing mode */
    isCanvas: boolean;
    /** Uniform composition instance for rendering */
    composition?: RootComponentInstance;
    /** Contentful environment for content fetching */
    contentfulEnvironment: ContentfulEnvironment;
    /** Personalization values known at the Bifrost template level */
    personalizationValues?: PersonalizationValues;
};
/**
 * Personalization values known at the Bifrost template level
 */
export type PersonalizationValues = {
    [UserSegmentTypes.CLUSTER_TAG]?: string;
};
// Default values for WAE context
const DEFAULT_VALUES: WAEContextType = {
    wacEnvironment: "development",
    uniformEnvironment: "production",
    uniformProjectId: "",
    isCanvas: false,
    composition: undefined,
    contentfulEnvironment: "master",
    personalizationValues: {}
};

/**
 * Get the WAC environment from environment variables with fallback
 */
const getWACEnvironment = (): WACEnvironment => {
    const env =
        process.env["CLIENT_LIBRARY_ENV"] ||
        process.env["NODE_ENV"] ||
        "development";

    switch (env) {
        case "development":
        case "dev":
        case "test": // Handle Jest test environment
            return "development";
        case "staging":
        case "stg":
            return "staging";
        case "production":
        case "prod":
            return "production";
        default:
            // Use development as default for testing and local development
            return "development";
    }
};

const WAEContext = createContext<WAEContextType | null>(null);

/**
 * Hook to access WAE context
 * @throws {Error} When used outside of WAEContextProvider
 * @returns {WAEContextType} The current WAE context
 */
export const useWAEContext = (): WAEContextType => {
    const context = useContext(WAEContext);
    if (!context) {
        throw new Error(
            "useWAEContext must be used within a WAEContextProvider. " +
                "Make sure your component is wrapped with <WAEContextProvider>."
        );
    }
    return context;
};

/**
 * Hook to safely access WAE context without throwing
 * @returns {WAEContextType | null} The current WAE context or null if not available
 */
export const useWAEContextSafe = (): WAEContextType | null => {
    return useContext(WAEContext);
};

/**
 * Hook to check if component is running in Canvas mode
 * @returns {boolean} True if in Canvas editing mode
 */
export const useIsCanvas = (): boolean => {
    const context = useWAEContext();
    return context.isCanvas;
};

/**
 * Hook to get current environment information
 * @returns {object} Environment information
 */
export const useEnvironmentInfo = () => {
    const context = useWAEContext();
    return {
        wacEnvironment: context.wacEnvironment,
        uniformEnvironment: context.uniformEnvironment,
        contentfulEnvironment: context.contentfulEnvironment,
        isProduction: context.wacEnvironment === "production",
        isDevelopment: context.wacEnvironment === "development",
        isStaging: context.wacEnvironment === "staging"
    };
};

/**
 * Props for WAEContextProvider component
 */
export type WAEContextProviderProps = PropsWithChildren & {
    /** WAC deployment environment (auto-detected if not provided) */
    wacEnvironment?: WACEnvironment;
    /** Uniform CMS environment */
    uniformEnvironment?: UniformEnvironment;
    /** Uniform project identifier */
    uniformProjectId?: string;
    /** Whether running in Uniform Canvas editing mode */
    isCanvas?: boolean;
    /** Uniform composition instance for rendering */
    composition?: RootComponentInstance;
    /** Contentful environment for content fetching */
    contentfulEnvironment?: ContentfulEnvironment;
    /** Personalization values known at the Bifrost template level */
    personalizationValues?: PersonalizationValues;
};

/**
 * WAE Context Provider Component
 *
 * Provides environment configuration and composition data to child components.
 * Automatically detects WAC environment from environment variables if not explicitly provided.
 *
 * @example
 * ```tsx
 * <WAEContextProvider
 *   wacEnvironment="staging"
 *   uniformProjectId="my-project"
 *   isCanvas={false}
 * >
 *   <App />
 * </WAEContextProvider>
 * ```
 */
export const WAEContextProvider = ({
    children,
    wacEnvironment,
    uniformEnvironment = DEFAULT_VALUES.uniformEnvironment,
    uniformProjectId = DEFAULT_VALUES.uniformProjectId,
    isCanvas = DEFAULT_VALUES.isCanvas,
    composition = DEFAULT_VALUES.composition,
    contentfulEnvironment = DEFAULT_VALUES.contentfulEnvironment,
    personalizationValues = DEFAULT_VALUES.personalizationValues
}: WAEContextProviderProps) => {
    // Auto-detect WAC environment if not provided
    const resolvedWacEnvironment = wacEnvironment || getWACEnvironment();

    const contextValue: WAEContextType = {
        wacEnvironment: resolvedWacEnvironment,
        uniformEnvironment,
        uniformProjectId,
        isCanvas,
        composition,
        contentfulEnvironment,
        personalizationValues
    };

    return (
        <WAEContext.Provider value={contextValue}>
            {children}
        </WAEContext.Provider>
    );
};
